<script setup lang="ts">
  import { computed, onMounted, onUnmounted, watch } from 'vue'
  import {
    PASSPORT_MASK_CONFIG,
    BIRTH_MIN_DATE,
    BIRTH_DATE_DEFAULT_VALUE,
  } from '@/shared/components/rebranding/VisitorForm/VisitorForm.constants'
  import { MAX_DATE } from '@/shared/constants/dates.constants'
  import { useI18n } from 'vue-i18n'
  import { useValidators } from '@/shared/composables/useValidators'
  import { useValidation } from '@qicgroupdoha/qic-use'
  import FlowCard from '@/shared/components/rebranding/FlowCard/FlowCard.vue'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import InputQC from '@qicgroupdoha/qic-connect-ds/components/InputQC'
  import InputDateQC from '@qicgroupdoha/qic-connect-ds/components/InputDateQC'
  import AlertQC from '@qicgroupdoha/qic-connect-ds/components/AlertQC'
  import { ERROR_NO_POLICY_FOUND, ERROR_TOO_MANY_REQUETS } from '@/shared/store/policy'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'

  interface Props {
    isLoading?: boolean
    policyNumber?: string
  }
  interface Emits {
    (e: 'proceed', passport: string, birthdate: Date): void
    (e: 'back'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    isLoading: false,
    policyNumber: '',
  })

  const emit = defineEmits<Emits>()
  const passport = defineModel<string>('passport', { default: '' })
  const birthdate = defineModel<Date>('birthdate', { default: null })
  const errorType = defineModel<
    Nullable<typeof ERROR_TOO_MANY_REQUETS | typeof ERROR_NO_POLICY_FOUND>
  >('errorType', { default: null })

  const gaService = useGoogleAnalytics()
  const { locale, t } = useI18n()
  const { required, passportValidator } = useValidators()

  const TOO_MANY_REQUESTS_COOLDOWN_MS = 60_000
  let tooManyRequestsCooldownTimer: ReturnType<typeof setTimeout> | undefined

  const validation = computed(() => ({
    passport: { ...passportValidator },
    birthdate: { required },
  }))

  const personValidationData = computed(() => ({
    passport: passport.value ?? '',
    birthdate: birthdate.value ?? '',
  }))

  const { v$, errors } = useValidation(validation, personValidationData)

  const errorMessage = computed(() =>
    errorType.value === ERROR_NO_POLICY_FOUND
      ? t(`qic_download_page.download_block.error.alert.no_policy.text`)
      : t(`qic_download_page.download_block.error.alert.to_many_requests.text`)
  )
  const disabledProceedButton = computed(() => errorType.value === ERROR_TOO_MANY_REQUETS)

  const resetNoPolicyFound = () => {
    if (errorType.value === ERROR_NO_POLICY_FOUND) {
      errorType.value = null
    }
  }

  const sendInputValidationError = () => {
    gaService.sendInputError(
      'passport',
      passport.value,
      (errors.passport?.[0] as string) ?? '',
      'policy_download',
      props.policyNumber
    )

    if (!props.policyNumber) {
      gaService.sendInputError(
        'birthDate',
        birthdate.value,
        (errors.birthDate?.[0] as string) ?? '',
        'policy_download',
        props.policyNumber
      )
    }
  }

  const onProceed = () => {
    gaService.sendPolicyEvents('click_download_policy', 'policy_download', props.policyNumber)
    v$.value.$touch()
    if (v$.value.$error || !passport.value || !birthdate.value) {
      sendInputValidationError()
      return
    }
    emit('proceed', passport.value, new Date(birthdate.value))
  }

  watch(
    () => errorType.value,
    (type) => {
      if (tooManyRequestsCooldownTimer) {
        clearTimeout(tooManyRequestsCooldownTimer)
        tooManyRequestsCooldownTimer = undefined
      }
      if (type === ERROR_TOO_MANY_REQUETS) {
        tooManyRequestsCooldownTimer = setTimeout(() => {
          errorType.value = null
          tooManyRequestsCooldownTimer = undefined
        }, TOO_MANY_REQUESTS_COOLDOWN_MS)
      }
    }
  )

  watch(passport, (newVal) => {
    if (newVal) {
      v$.value.passport.$reset()
      resetNoPolicyFound()
    }
  })

  watch(birthdate, (newVal) => {
    if (newVal) {
      v$.value.birthdate.$reset()
      resetNoPolicyFound()
    }
  })

  onMounted(() => {
    gaService.sendPolicyEvents('view_policy_download', 'policy_download', props.policyNumber)
  })
  
  onUnmounted(() => {
    if (tooManyRequestsCooldownTimer) {
      clearTimeout(tooManyRequestsCooldownTimer)
    }
  })
</script>

<template>
  <FlowCard
    :title="$t('qic_download_page.download_block.header')"
    :subtitle="$t('qic_download_page.download_block.annotation')"
    class="policy-download-qc"
    with-back-button
    :title-wrap="false"
    :with-button="false"
    @back="emit('back')"
  >
    <WrapperQC direction="column" gap="var(--spacing-6)">
      <WrapperQC direction="column" gap="var(--spacing-5)">
        <!-- @vue-ignore -->
        <InputQC
          v-model="passport"
          :errors="errors.passport"
          name="passport"
          :label="$t('qlm_passport')"
          :mask-config="PASSPORT_MASK_CONFIG"
          :max-length="20"
          :loading="isLoading"
          :container-attrs="{
            'data-test': 'passport-input',
          }"
          data-test="passport-input-inner"
        />

        <!-- @vue-ignore -->
        <InputDateQC
          v-model="birthdate"
          :label="$t('qic_user_dob')"
          :min-date="BIRTH_MIN_DATE"
          :max-date="MAX_DATE"
          :errors="errors.birthdate"
          :locale="locale"
          :calendar-props="{
            startDate: birthdate ?? BIRTH_DATE_DEFAULT_VALUE,
          }"
          :input-props="{
            loading: isLoading,
            containerAttrs: {
              'data-test': 'birth-date-input',
            },
            name: 'birthDate',
          }"
          allow-manual-input
          data-test="birth-date-input-inner"
        />
      </WrapperQC>

      <AlertQC v-if="errorType" :subtitle="errorMessage" variant="error" with-icon />

      <ButtonQC
        :icon-after-content="locale === 'en' ? 'arrow-narrow-right' : 'arrow-narrow-left'"
        :loading="isLoading"
        :disabled="disabledProceedButton"
        @click="onProceed"
      >
        {{ $t('qic_download_page.download_block.continue.button') }}
      </ButtonQC>

      <p class="policy-download-qc__annotation text-small-regular">
        {{ $t('qic_download_page.download_block.form_annotation') }}
      </p>
    </WrapperQC>
  </FlowCard>
</template>

<style lang="scss" scoped>
  @use '@scss/utils.scss';

  .policy-download-qc {
    padding: var(--spacing-8);

    @include utils.mobile-flow {
      padding: var(--spacing-8) var(--spacing-5);
    }
  }

  .policy-download-qc__annotation {
    color: var(--colorTxtSrfSecondary);
    text-align: center;
  }
</style>
